export interface StoredTenant {
  id: string;
  name: string;
  role: string;
}

const legacyIdentityTokenKey = 'bitrix-node.identity-token';
const legacyTenantTokenKey = 'bitrix-node.tenant-token';
const activeTenantKey = 'bitrix-node.active-tenant';
export const sparkOneAuthChangedEvent = 'sparkone:auth-changed';

let identityToken: string | null = null;
let tenantToken: string | null = null;
let authGeneration = 0;

function browserStorage() {
  return typeof window === 'undefined' ? null : window.localStorage;
}

export function purgeLegacyBearerStorage() {
  const storage = browserStorage();
  storage?.removeItem(legacyIdentityTokenKey);
  storage?.removeItem(legacyTenantTokenKey);
}

purgeLegacyBearerStorage();

export function getAuthGeneration() {
  return authGeneration;
}

export function isAuthGenerationCurrent(generation: number) {
  return generation === authGeneration;
}

export function getIdentityToken() {
  purgeLegacyBearerStorage();
  return identityToken;
}

export function setIdentityToken(token: string, generation = authGeneration) {
  if (!isAuthGenerationCurrent(generation)) return false;
  purgeLegacyBearerStorage();
  identityToken = token;
  return true;
}

export function getTenantToken() {
  purgeLegacyBearerStorage();
  return tenantToken;
}

export function setTenantToken(token: string, generation = authGeneration) {
  if (!isAuthGenerationCurrent(generation)) return false;
  purgeLegacyBearerStorage();
  tenantToken = token;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(sparkOneAuthChangedEvent));
  }
  return true;
}

export function getBearerToken() {
  return getTenantToken();
}

export function getActiveTenant(): StoredTenant | null {
  const value = browserStorage()?.getItem(activeTenantKey);
  if (!value) return null;
  try {
    return JSON.parse(value) as StoredTenant;
  } catch {
    browserStorage()?.removeItem(activeTenantKey);
    return null;
  }
}

export function getActiveTenantId() {
  return getActiveTenant()?.id ?? null;
}

export function setActiveTenant(
  tenant: StoredTenant,
  generation = authGeneration,
) {
  if (!isAuthGenerationCurrent(generation)) return false;
  browserStorage()?.setItem(activeTenantKey, JSON.stringify(tenant));
  return true;
}

function invalidateSession(
  clearTenantMetadata: boolean,
  expectedGeneration?: number,
) {
  if (
    expectedGeneration !== undefined &&
    !isAuthGenerationCurrent(expectedGeneration)
  )
    return null;

  authGeneration += 1;
  // Resource owners get one synchronous chance to revoke authenticated
  // authority while the old in-memory bearer remains readable.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('sparkone:auth-lost'));
  }
  identityToken = null;
  tenantToken = null;
  purgeLegacyBearerStorage();
  if (clearTenantMetadata) browserStorage()?.removeItem(activeTenantKey);
  return authGeneration;
}

export function clearSessionTokens(expectedGeneration?: number) {
  return invalidateSession(false, expectedGeneration);
}

export function clearAuthStorage(expectedGeneration?: number) {
  return invalidateSession(true, expectedGeneration);
}
